import { SUPPORTED_LANGUAGES } from '../common/constants';

const LOCALE_KEY = 'gb_preferred_locale';
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year in seconds

/**
 * Utility for managing language preference in both localStorage and cookies
 * This ensures server-side code can access user language preference via cookies
 * while maintaining client-side persistence via localStorage
 */
class LanguagePreferenceManager {
  /**
   * Get stored language preference from localStorage, fallback to cookies, then 'en'
   * @returns {string} Language code (e.g., 'en', 'fr')
   */
  // eslint-disable-next-line class-methods-use-this
  getStoredLanguagePreference() {
    try {
      // First check localStorage
      const storedLanguage = localStorage.getItem(LOCALE_KEY);

      // Reset localStorage if we find an invalid value
      if (storedLanguage && !Object.values(SUPPORTED_LANGUAGES).includes(storedLanguage.toLowerCase())) {
        localStorage.setItem(LOCALE_KEY, 'en');
      } else if (storedLanguage) {
        return storedLanguage;
      }

      // Fallback to reading from cookies if localStorage is empty
      const cookieLanguage = this.getCookieValue(LOCALE_KEY);
      if (cookieLanguage) {
        // If we found a language in cookies but not localStorage, sync them
        localStorage.setItem(LOCALE_KEY, cookieLanguage);
        return cookieLanguage;
      }

      return SUPPORTED_LANGUAGES.ENGLISH;
    } catch {
      // If localStorage fails, try reading from cookies
      try {
        const cookieLanguage = this.getCookieValue(LOCALE_KEY);
        return cookieLanguage || SUPPORTED_LANGUAGES.ENGLISH;
      } catch {
        return SUPPORTED_LANGUAGES.ENGLISH;
      }
    }
  }

  /**
   * Set language preference in both localStorage and cookie
   * This ensures both client and server can access the preference
   * @param {string} language - Language code (e.g., 'en', 'fr')
   */
  setLanguagePreference(language) {
    try {
      if (Object.values(SUPPORTED_LANGUAGES).includes(language.toLowerCase())) {
        // Update localStorage for client-side persistence
        localStorage.setItem(LOCALE_KEY, language);

        // Update cookie for server-side access
        this.setCookie(LOCALE_KEY, language, COOKIE_MAX_AGE);
      }
    } catch (error) {
      // Ignore storage errors
    }
  }

  /**
   * Get a cookie value by name
   * @param {string} name - Cookie name
   * @returns {string|null} Cookie value or null if not found
   */
  // eslint-disable-next-line class-methods-use-this
  getCookieValue(name) {
    if (typeof document === 'undefined') return null;

    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  /**
   * Set a cookie
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {number} maxAge - Max age in seconds
   */
  // eslint-disable-next-line class-methods-use-this
  setCookie(name, value, maxAge) {
    if (typeof document === 'undefined') return;

    document.cookie = `${name}=${value}; path=/; max-age=${maxAge}`;
  }

  /**
   * Get language preference from cookie (used by server-side code)
   * @param {string} cookieString - Raw cookie string from request headers
   * @returns {string|null} Language code or null if not found
   */
  static getLanguageFromCookies(cookieString) {
    if (!cookieString) return null;

    const match = cookieString.match(/gb_preferred_locale=([^;]+)/);
    return match ? match[1] : null;
  }

  /**
   * Remove language preference from both localStorage and cookie
   */
  clearLanguagePreference() {
    try {
      localStorage.removeItem(LOCALE_KEY);
      this.setCookie(LOCALE_KEY, '', -1); // Set expiry to past date to delete
    } catch (error) {
      // Ignore storage errors
    }
  }
}

// Export singleton instance
export default new LanguagePreferenceManager();

// Export class for testing
export { LanguagePreferenceManager };

// Export constants for consistency
export { LOCALE_KEY, COOKIE_MAX_AGE };
